import type { Patch } from "./types";
import { createSource, type VoiceSource } from "./VoiceSource";

// One sounding note. Graph: source -> filter -> tremolo -> amp(ADSR) -> out.
// The LFO is routed to a single target parameter chosen by the patch.
export class Voice {
  private readonly ctx: BaseAudioContext;
  private readonly source: VoiceSource;
  private readonly filter: BiquadFilterNode;
  private readonly tremolo: GainNode;
  private readonly amp: GainNode;
  private readonly lfo?: OscillatorNode;
  private readonly lfoDepth?: GainNode;
  private readonly release: number;
  private readonly shaper?: WaveShaperNode;
  private readonly pitchEnv?: Patch["pitchEnv"];
  private readonly fm?: Patch["fm"];
  private readonly modOsc?: OscillatorNode;
  private readonly modGain?: GainNode;
  private readonly filterEnv?: Patch["filterEnv"];
  private readonly baseCutoff: number;

  constructor(ctx: BaseAudioContext, patch: Patch, destination: AudioNode) {
    this.ctx = ctx;
    this.release = patch.ampEnv.release;
    this.pitchEnv = patch.pitchEnv;
    this.filterEnv = patch.filterEnv;
    this.baseCutoff = patch.filter.cutoff;

    this.source = createSource(ctx, patch.oscillator.type);
    this.source.detune.value = patch.oscillator.detune;

    // Optional FM operator — a sine modulator summed into the carrier's
    // frequency. Its rate/depth depend on the played note, so they're set in
    // start(); here we just build and wire the nodes.
    if (patch.fm && patch.fm.depth > 0) {
      this.fm = patch.fm;
      this.modOsc = new OscillatorNode(ctx, { type: "sine" });
      this.modGain = new GainNode(ctx, { gain: 0 });
      this.modOsc.connect(this.modGain);
      this.modGain.connect(this.source.frequency);
    }
    if (this.source.width) {
      // Convert duty cycle 0..1 to the shaper's -1..1 offset (0.5 -> 0).
      this.source.width.value = patch.oscillator.pulseWidth * 2 - 1;
    }

    this.filter = new BiquadFilterNode(ctx, {
      type: patch.filter.type,
      frequency: patch.filter.cutoff,
      Q: patch.filter.q,
    });

    this.tremolo = new GainNode(ctx, { gain: 1 });
    this.amp = new GainNode(ctx, { gain: 0 });

    // Optional waveshaper drive after the filter (grit / "metal" / punch).
    this.source.output.connect(this.filter);
    if (patch.drive && patch.drive > 0) {
      this.shaper = new WaveShaperNode(ctx, { curve: driveCurve(patch.drive), oversample: "2x" });
      this.filter.connect(this.shaper);
      this.shaper.connect(this.tremolo);
    } else {
      this.filter.connect(this.tremolo);
    }
    this.tremolo.connect(this.amp);
    this.amp.connect(destination);

    if (patch.lfo.target !== "off") {
      this.lfo = new OscillatorNode(ctx, {
        type: patch.lfo.shape,
        frequency: patch.lfo.rate,
      });
      this.lfoDepth = new GainNode(ctx, { gain: patch.lfo.depth });
      this.lfo.connect(this.lfoDepth);
      this.routeLfo(patch);
    }
  }

  private routeLfo(patch: Patch): void {
    if (!this.lfoDepth) return;
    switch (patch.lfo.target) {
      case "pitch":
        this.lfoDepth.connect(this.source.detune);
        break;
      case "amplitude":
        this.lfoDepth.connect(this.tremolo.gain);
        break;
      case "filter":
        this.lfoDepth.connect(this.filter.frequency);
        break;
      case "pulseWidth":
        if (this.source.width) this.lfoDepth.connect(this.source.width);
        break;
    }
  }

  start(when: number, frequencyHz: number, env: Patch["ampEnv"], velocity = 1): void {
    const vel = Number.isFinite(velocity) ? Math.max(0, Math.min(1, velocity)) : 1;
    const f = this.source.frequency;
    const pe = this.pitchEnv;
    if (pe && pe.amount !== 0) {
      // Sweep from `amount` semitones above the note down to it (kick / zap).
      const startHz = Math.max(1, frequencyHz * Math.pow(2, pe.amount / 12));
      f.cancelScheduledValues(when);
      f.setValueAtTime(startHz, when);
      f.exponentialRampToValueAtTime(Math.max(1, frequencyHz), when + Math.max(0.005, pe.decay));
    } else {
      f.setValueAtTime(frequencyHz, when);
    }
    if (this.modOsc && this.modGain && this.fm) {
      // Modulator at ratio×note; depth scales with pitch so the timbre is
      // consistent across the keyboard (depth = modulation index).
      this.modOsc.frequency.setValueAtTime(frequencyHz * this.fm.ratio, when);
      this.modGain.gain.setValueAtTime(frequencyHz * this.fm.depth, when);
      this.modOsc.start(when);
    }
    this.source.start(when);
    this.lfo?.start(when);

    // Filter envelope — snap the cutoff up `amount` Hz, then slide back. The LFO
    // (if it targets the filter) rides on top as an additive input.
    if (this.filterEnv && this.filterEnv.amount !== 0) {
      const fc = this.filter.frequency;
      const peak = Math.max(20, Math.min(20000, this.baseCutoff + this.filterEnv.amount));
      fc.cancelScheduledValues(when);
      fc.setValueAtTime(peak, when);
      fc.linearRampToValueAtTime(this.baseCutoff, when + Math.max(0.005, this.filterEnv.decay));
    }

    const g = this.amp.gain;
    const peakAt = when + Math.max(env.attack, 0.001);
    const sustainAt = peakAt + Math.max(env.decay, 0.001);
    g.cancelScheduledValues(when);
    g.setValueAtTime(0, when);
    g.linearRampToValueAtTime(vel, peakAt);
    g.linearRampToValueAtTime(env.sustain * vel, sustainAt);
  }

  stop(when: number): void {
    const g = this.amp.gain;
    if (typeof g.cancelAndHoldAtTime === "function") {
      g.cancelAndHoldAtTime(when);
    } else {
      g.cancelScheduledValues(when);
      g.setValueAtTime(g.value, when);
    }
    const endAt = when + Math.max(this.release, 0.001);
    g.linearRampToValueAtTime(0, endAt);

    const stopAt = endAt + 0.02;
    this.source.stop(stopAt);
    this.lfo?.stop(stopAt);
    this.modOsc?.stop(stopAt);
    this.scheduleCleanup(stopAt);
  }

  /** Live cutoff tweaking while a note is held. */
  setFilterCutoff(hz: number): void {
    this.filter.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.01);
  }

  /** Glide the pitch of a held note (used by the frequency ribbon). */
  setFrequency(hz: number): void {
    this.source.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.02);
  }

  private scheduleCleanup(stopAt: number): void {
    const ms = Math.max(0, (stopAt - this.ctx.currentTime) * 1000) + 50;
    setTimeout(() => {
      this.source.output.disconnect();
      this.filter.disconnect();
      this.shaper?.disconnect();
      this.tremolo.disconnect();
      this.amp.disconnect();
      this.lfoDepth?.disconnect();
      this.modOsc?.disconnect();
      this.modGain?.disconnect();
    }, ms);
  }
}

// Soft-clip curve for the optional drive stage. amount 0..1 → harder clipping.
// Output stays bounded to ±1 (no makeup-gain blowups; the master compressor
// catches the rest). `(1+k)·x / (1+k·|x|)` is a cheap, smooth saturator.
function driveCurve(amount: number): Float32Array {
  const k = Math.max(0, Math.min(1, amount)) * 80;
  const n = 1024;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  return curve;
}
